import { connect } from 'react-redux';

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import replace from 'lodash/replace';
import isEmpty from 'lodash/isEmpty';
import loadable from '@loadable/component';

import classNames from 'classnames';
import Waypoint from 'react-waypoint';

import { gettext, translationPropType } from '@eb/i18n';

import {
    clickOnViewEvent,
    clickOnSeeMore,
    clickOnOnboardingCard,
} from '../../redux/events';

import {
    BUCKET_CONFIG_PROPTYPE,
    EVENTS_PROPTYPES,
    SEE_MORE_PROPTYPE,
    EVENT_SEARCH_PROPTYPES,
} from '../../../../constants/propTypes';
import {
    BEST_OF_CITY_EVENTS,
    POPULAR_EVENTS,
    PRIMARY_BUCKET,
} from '../../../../constants/constants';
import { FREE_TAB, ALL_TAB } from '../../../../constants/tabConfig';
import { BASE_SEASONAL_CONTENT_PROPTYPES } from '../../../../constants/seasonal';
import { getBucketConfig, getEventsLayout } from '../../utils';
import { ConsumerCards } from '../../components/ConsumerCards';

import { useWindowWidth } from './EventsBucketContainer.hooks';
import {
    MOBILE_DEFAULT_LAYOUT_CONFIG,
    DESKTOP_DEFAULT_LAYOUT_CONFIG,
    LAYOUT_WIDTH_MAP,
    ONLINE_EVENTS,
} from './EventsBucketContainer.constants';

import './EventsBucketPremium.scss';

const SeeMoreOfBucket = loadable(() => import('./SeeMoreOfBucket'));
const EmptyState = loadable(() => import('../../components/EmptyState'), {
    resolveComponent: (components) => components.EmptyState,
});

const _isInOddLayout = (windowWidth) =>
    windowWidth >= LAYOUT_WIDTH_MAP.odd && windowWidth < LAYOUT_WIDTH_MAP.even;

const BucketHeader = (props) => {
    if (!props.name && !props.subtitle) {
        return null;
    }

    let titleComponent = null;
    let subtitleComponent = null;

    if (props.name) {
        let parsedName = props.name;

        const titleClassNames = classNames(
            'eds-text-color--grey-900',
            'eds-text-hs',
        );

        if (props.name.lazyIncludeLocation) {
            parsedName = props.name.lazyIncludeLocation(props.currentPlace);
        }

        if (props.currentPlace === ONLINE_EVENTS && props.isPrimaryBucket) {
            parsedName = gettext('Online Events');
        }

        titleComponent = <span className={titleClassNames}>{parsedName}</span>;
    }

    if (props.subtitle && props.subtitle.lazyIncludeLocation) {
        const parsedSubtitle = props.subtitle.lazyIncludeLocation(
            props.currentPlace,
        );

        subtitleComponent = (
            <span className="eds-text-bm eds-text-color--grey-600">
                {parsedSubtitle}
            </span>
        );
    }

    const bucketHeaderClassNames = classNames(
        'eds-l-pad-bot-6',
        {
            'eds-l-sm-pad-bot-10 eds-l-sw-pad-bot-8 pad-bot-mn-up': !props.isSpecialBucket,
            'pad-bot-sm-up--premium': props.isSpecialBucket,
        },
        'eds-l-mn-pad-hor-3',
        'eds-l-md-pad-hor-3',
        'eds-l-mw-pad-hor-3',
        'eds-l-ln-pad-hor-3',
        'eds-l-lg-pad-hor-3',
        'eds-l-lw-pad-hor-3',
    );

    let bucketHeader = null;

    if (subtitleComponent || titleComponent) {
        bucketHeader = (
            <div
                className={bucketHeaderClassNames}
                data-testid="feed-bucket-header"
            >
                {titleComponent}
                <div>{subtitleComponent}</div>
            </div>
        );
    }

    return bucketHeader;
};

const EventsBucket = ({
    events = [],
    searchCriteria = {},
    bucketConfig,
    shouldUseWaypoints,
    ...props
}) => {
    const [isVisible, setVisible] = React.useState(!shouldUseWaypoints);
    const [animateIn, setAnimateIn] = React.useState(false);

    useEffect(() => {
        if (isVisible && shouldUseWaypoints && !animateIn) {
            setAnimateIn(true);
        }
    }, [isVisible, shouldUseWaypoints, animateIn]);

    const windowWidth = useWindowWidth(100);
    const isMobile = windowWidth <= 660;

    const bucketClassNames = React.useMemo(() => {
        const bucketClassNamesDesktop = classNames(
            'feed-events-bucket',
            `feed-events--${props.bucketKey}`,
            {
                'feed-events-bucket--animated': shouldUseWaypoints,
                'feed-events-bucket--animate': animateIn,
            },
        );
        const bucketClassNamesMobile = classNames(
            bucketClassNamesDesktop,
            'feed-events-bucket--mobile',
        );

        return isMobile ? bucketClassNamesMobile : bucketClassNamesDesktop;
    }, [isMobile, animateIn, props.bucketKey, shouldUseWaypoints]);

    const layoutConfig = React.useMemo(() => {
        const overwrite = isMobile
            ? 'overwrite.props.layout.mobile.events'
            : 'overwrite.props.layout.desktop.events';
        const defaultConfig = isMobile
            ? MOBILE_DEFAULT_LAYOUT_CONFIG
            : DESKTOP_DEFAULT_LAYOUT_CONFIG;

        return getBucketConfig({
            bucketConfig: get(bucketConfig, overwrite, {}),
            defaultConfig,
        });
    }, [isMobile, bucketConfig]);

    const eventGroups = React.useMemo(() => {
        if (isMobile) {
            return getEventsLayout({
                events,
                bucketConfig: layoutConfig,
            });
        }

        return getEventsLayout({
            events,
            bucketConfig: layoutConfig,
        });
    }, [isMobile, layoutConfig, events]);

    if (isEmpty(events)) {
        return (
            <EmptyState
                bucketKey={props.bucketKey}
                emptyState={bucketConfig.emptyState}
                isAuthenticated={props.isAuthenticated}
            />
        );
    }

    return (
        <Waypoint onEnter={() => setVisible(true)} bottomOffset={'-75px'}>
            {isVisible ? (
                <div className={bucketClassNames}>
                    <BucketHeader
                        name={props.name}
                        subtitle={props.subtitle}
                        currentPlace={props.currentPlace}
                        isPrimaryBucket={props.bucketKey === PRIMARY_BUCKET}
                        isSpecialBucket={[
                            BEST_OF_CITY_EVENTS,
                            POPULAR_EVENTS,
                        ].includes(props.bucketKey)}
                    />

                    <ConsumerCards
                        bucketKey={props.bucketKey}
                        isAuthenticated={props.isAuthenticated}
                        isFreeTab={searchCriteria.price === FREE_TAB}
                        isFirstBucket={!props.tabKey === ALL_TAB}
                        isInOddLayout={_isInOddLayout(windowWidth)}
                        onClickOnBoardingCard={props.onClickOnBoardingCard}
                        onViewEvent={props.onViewEvent}
                        shouldShowOnboardingCard={get(
                            bucketConfig,
                            'shouldShowOnboardingCard',
                            false,
                        )}
                        bucketLayoutConfig={layoutConfig}
                        eventsGroups={eventGroups}
                        cardType="grid"
                    />

                    {props.shouldShowSeeMore && (
                        <SeeMoreOfBucket
                            bucketKey={props.bucketKey}
                            seeMore={props.seeMore}
                            onSeeMore={props.onSeeMore}
                            searchCriteria={searchCriteria}
                            shouldShowSeeMore={true}
                            locationSlug={props.locationSlug}
                            tabKey={props.tabKey}
                            userId={props.userId}
                            seasonalContent={props.seasonalContent}
                        />
                    )}
                </div>
            ) : (
                <div className={bucketClassNames} />
            )}
        </Waypoint>
    );
};

EventsBucket.propTypes = {
    onSeeMore: PropTypes.func.isRequired,

    bucketConfig: BUCKET_CONFIG_PROPTYPE,
    currentPlace: PropTypes.string,
    bucketKey: PropTypes.string,
    events: EVENTS_PROPTYPES,
    isAuthenticated: PropTypes.bool,
    isMobile: PropTypes.bool,
    name: translationPropType,
    seeMore: SEE_MORE_PROPTYPE,
    subtitle: translationPropType,
    shouldUseWaypoints: PropTypes.bool,
    shouldShowSeeMore: PropTypes.bool,
    searchCriteria: EVENT_SEARCH_PROPTYPES,
    locationSlug: PropTypes.string,
    tabKey: PropTypes.string,
    userId: PropTypes.string,
    onClickOnBoardingCard: PropTypes.func,
    seasonalContent: PropTypes.arrayOf(
        PropTypes.shape(BASE_SEASONAL_CONTENT_PROPTYPES),
    ),
};

const _mapStateToProps = (state) => ({
    isMobile: state.app.isMobile,
    isAuthenticated: state.user.isAuthenticated,
    searchCriteria: state.content.flatBucket
        ? state.content.flatBucket.eventSearch
        : { dates: ['current_future'] },
    locationSlug: state.location.slug,
    currentPlace: state.location.currentPlace,
    tabKey: state.content.browseState.tabKey,
    userId: state.user.publicId,
    seasonalContent: state.content.seasonal,
});

const _mapDispatchToProps = {
    onSeeMore: clickOnSeeMore,
    onViewEvent: clickOnViewEvent,
    onClickOnBoardingCard: clickOnOnboardingCard,
};

export default connect(_mapStateToProps, _mapDispatchToProps)(EventsBucket);
